import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import cn from "classnames";
import { Link } from "react-router-dom";

// components
// import SearchInput from "components/LibraryComponents/Components/SearchInput/SearchInput";
import SearchInput from "components/LibraryComponents/Components/SearchInput";
import SamEntityTable from "components/SamEntityTable/SamEntityTable";
import DisplayValue from "components/BusinessProfileSections/DisplayValue";
import { Loader } from "components/icons";
import { Alert } from "@trussworks/react-uswds";
import BusinessTypes from "./BusinessTypes";

// icons
import { CheckIcon, XIcon } from "components/icons";
import { FaArrowLeft } from "react-icons/fa";

// store
import useAuthStore from "store/useAuthStore";

// utils
import {
	getLegalStructureName,
	legalStructureValid,
	get8HBusinessTypes,
	getPurposeOfRegistration,
	formatSamEntityDate,
	getSamEntityDate,
	activationThreeDaysAgo
} from "utils/helper";

// query
import { useSamEntityQuery } from "./query";

// styles
import styles from './SamEntityPage.module.scss';

export default function SamEntityPage() {
	const { user } = useAuthStore();
	const navigate = useNavigate();

	// Redirect non-admin users to home
	useEffect(() => {
		if (!user || user.role !== "admin") {
			navigate("/");
		}
	}, [user, navigate]);

	const { uei, cage_code } = useParams();
	const { data, isPending, errorType, dataUpdatedAt } = useSamEntityQuery(uei, cage_code);
	// console.log(data)

	const entity = data?.entity
	const isLoaded = data?.isLoaded

	const hasNaics = entity?.naics_code_string?.includes("Y")
	const hasNaicsException = entity?.naics_exception_string?.includes("Y")
	// console.log(hasNaicsException)

	const registrationDate = formatSamEntityDate(entity?.registration_date);
	const expirationDate = formatSamEntityDate(entity?.expiration_date);
	const lastUpdateDate = formatSamEntityDate(entity?.last_update_date);
	const activationDate = formatSamEntityDate(entity?.activation_date);

	const purposeOfRegistrationValid = entity?.purpose_of_registration === "Z2" ? true : false
	const entityStructureValid = legalStructureValid(entity?.entity_structure);
	const is8H = entity?.entity_structure === "8H" ? true : false;
	const entity8HBusinessTypes = get8HBusinessTypes(entity?.business_type_string);

	const expirationInFuture = getSamEntityDate(entity?.expiration_date) > new Date();
	const activatedThreeDaysAgo = activationThreeDaysAgo(entity?.activation_date);

	const noPublicDisplayText = entity?.no_public_display_flag === true ? <p className={styles["value"]}>Yes: <strong>not</strong> searchable by the public</p> : <p className={styles["value"]}>No: <strong>searchable</strong> by the public</p>;

	const location = window.location.origin;
	const profileLink = `${location}/profile/${entity?.uei}/${entity?.cage_code}`;

	const handleBackTo = () => {
		navigate("/sam-entity");
	};

	if (errorType === "NOT_AUTHORIZED") {
		return (
			<>
				<Helmet>
					<title>Not authorized | SAM Entity | SBA Small Business Search</title>
				</Helmet>
				<div className={styles["error-wrapper"]}>
					<Alert type="error" headingLevel="h6" className={styles["alert-wrapper"]}>
						<p>You are not authorized to view this entity. Please contact your administrator if you believe this is an error.</p>
					</Alert>
				</div>
			</>
		);
	}

	if (isPending || (!isPending && !errorType && !entity)) {
		// if is pending, or store is not yet updated
		return (
			<>
				<Helmet>
					<title>Loading...</title>
				</Helmet>
				<div className={styles["loader-root"]}>
					<Loader />
				</div>
			</>
		);
	}

	const metaTitle = entity?.legal_business_name
		? `${entity.legal_business_name} - SAM Entity | SBA Small Business Search`
		: "SAM Entity | SBA Small Business Search";

	return (
		<div className={styles["sam-entity-page-root"]}>
			<Helmet>
				<title>{metaTitle}</title>
			</Helmet>
			{entity && (
				<>
					<div className={styles['back-btn-wrapper']}>
						<button className={styles["back-btn"]} onClick={handleBackTo}>
							<FaArrowLeft
								color="#005ea2"
								aria-label="Back to advanced search page"
							/>
							<span>Back to results</span>
						</button>
					</div>
					<div className={styles["basic-details-card"]}>
						<div className={styles["left"]}>
							<h1 className={styles["title"]}>
								{entity?.legal_business_name}
							</h1>

							<div className={cn(styles["group"], styles["mt-2"])}>
								<h5 className={cn(styles["label"], styles["bold"])}>UEI:</h5>
								<DisplayValue
									messageElement="p"
									className={styles["value"]}
									value={data?.entity.uei}
									message="Unknown"
								/>
							</div>
							<div className={styles["group"]}>
								<h5 className={cn(styles["label"], styles["bold"])}>CAGE code:</h5>
								<DisplayValue
									messageElement="p"
									value={data?.entity.cage_code}
									className={styles["value"]}
									message="Unknown"
								/>
							</div>

						</div>
					</div>

					{!isLoaded && (
						<div className={styles["alert-wrapper"]}>
							<Alert
								type="info"
								headingLevel="h6"
								className={styles["alert-wrapper"]}
							>
								<p>This business is <strong>not</strong> loaded into SBS</p>
							</Alert>
						</div>
					)}
					{isLoaded && (
						<div className={styles["alert-wrapper"]}>
							<Alert
								type="success"
								headingLevel="h6"
								className={styles["alert-wrapper"]}
							>
								<p>This business is loaded into SBS. Profile here: <Link to={profileLink}>{profileLink}</Link></p>
							</Alert>
						</div>
					)}

					<div className={styles["content"]}>

						<h2>SAM Entity Data</h2>

						<div className={styles["dates-wrapper"]}>
							<div className={styles["date-item"]}>
								<div className={styles["content-group"]}>
									<h5 className={styles["label"]}>Registration Date</h5>
									<DisplayValue
										messageElement="code"
										className={styles["value"]}
										value={registrationDate}
									/>
								</div>
							</div>
							<div className={styles["date-item"]}>
								<div className={styles["content-group"]}>
									<h5 className={styles["label"]}>Expiration Date</h5>
									<DisplayValue
										messageElement="code"
										className={styles["value"]}
										value={expirationDate}
									/>
								</div>
								{expirationInFuture && (
									<div className={styles["status-wrapper"]}>
										<CheckIcon height="24" width="24" color="#00A91C" />
										<p>In the future</p>
									</div>
								)}
								{!expirationInFuture && (
									<div className={styles["status-wrapper"]}>
										<XIcon height="24" width="24" color="#D54309" />
										<p>In the past</p>
									</div>
								)}
							</div>
							<div className={styles["date-item"]}>
								<div className={styles["content-group"]}>
									<h5 className={styles["label"]}>Last Updated Date</h5>
									<DisplayValue
										messageElement="code"
										className={styles["value"]}
										value={lastUpdateDate}
									/>
								</div>
							</div>
							<div className={styles["date-item"]}>
								<div className={styles["content-group"]}>
									<h5 className={styles["label"]}>Activation Date</h5>
									<DisplayValue
										messageElement="code"
										className={styles["value"]}
										value={activationDate}
									/>
								</div>
								{activatedThreeDaysAgo && (
									<div className={styles["status-wrapper"]}>
										<CheckIcon height="24" width="24" color="#00A91C" />
										<p>3+ days ago</p>
									</div>
								)}
								{!activatedThreeDaysAgo && (
									<div className={styles["status-wrapper"]}>
										<XIcon height="24" width="24" color="#D54309" />
										<p>{"<"}3 days ago</p>
									</div>
								)}
							</div>
						</div>

						<div className={styles["content-group"]}>
							<h5 className={styles["label"]}>Purpose of registration</h5>
							<div className={cn(styles["status-wrapper"], styles['inline'])}>
								{purposeOfRegistrationValid && (
									<CheckIcon height="24" width="24" color="#00A91C" />
								)}
								{!purposeOfRegistrationValid && (
									<XIcon height="24" width="24" color="#D54309" />
								)}
								<DisplayValue
									messageElement="code"
									className={styles["value"]}
									value={entity?.purpose_of_registration}
								/>
								<p>{getPurposeOfRegistration(entity?.purpose_of_registration)}</p>
							</div>
						</div>

						{!is8H && (
							<div className={styles["content-group"]}>
								<h5 className={styles["label"]}>Entity structure</h5>
								<div className={cn(styles["status-wrapper"], styles['inline'])}>
									{entityStructureValid && (
										<CheckIcon height="24" width="24" color="#00A91C" />
									)}
									{!entityStructureValid && (
										<XIcon height="24" width="24" color="#D54309" />
									)}
									<DisplayValue
										messageElement="code"
										className={styles["value"]}
										value={entity?.entity_structure}
									/>
									<p>{getLegalStructureName(entity?.entity_structure)}</p>
								</div>
							</div>
						)}

						{is8H && (
							<div className={styles["content-group"]}>
								<h5 className={styles["label"]}>Entity structure</h5>
								<div className={cn(styles["status-wrapper"], styles['inline'])}>
									<DisplayValue
										messageElement="code"
										className={styles["value"]}
										value={entity?.entity_structure}
									/>
									<p>{getLegalStructureName(entity?.entity_structure)}</p>
								</div>

								<div className={styles['business-types-wrapper']}>
									<BusinessTypes businessTypes={entity8HBusinessTypes} />
								</div>
							</div>
						)}

						<div className={styles["content-group"]}>
							<h5 className={styles["label"]}>NAICS Code String</h5>
							<DisplayValue
								messageElement="code"
								className={styles["value"]}
								value={entity?.naics_code_string}
							/>
							{hasNaics && (
								<div className={styles["status-wrapper"]}>
									<CheckIcon height="24" width="24" color="#00A91C" />
									<p>Yes - at least one Y</p>
								</div>
							)}
							{!hasNaics && (
								<div className={styles["status-wrapper"]}>
									<XIcon height="24" width="24" color="#D54309" />
									<p>No Ys - NAICS Exception Code String needs at least one Y</p>
								</div>
							)}
						</div>



						<div className={styles["content-group"]}>
							<h5 className={styles["label"]}>NAICS Exception Code String</h5>
							<DisplayValue
								messageElement="code"
								className={styles["value"]}
								value={entity?.naics_exception_string}
							/>
							{hasNaicsException && (
								<div className={styles["status-wrapper"]}>
									<CheckIcon height="24" width="24" color="#00A91C" />
									<p>Yes - at least one Y</p>
								</div>
							)}
							{!hasNaicsException && (
								<div className={styles["status-wrapper"]}>
									<XIcon height="24" width="24" color="#D54309" />
									<p>No Ys</p>
								</div>
							)}
						</div>

						<div className={styles["content-group"]}>
							<h5 className={styles["label"]}>YNPD: Yes to no public display</h5>
							{noPublicDisplayText}
						</div>
					</div>
				</>
			)}
			{isPending &&
				<div className={styles["loader-root"]}>
					<Loader />
				</div>
			}
		</div>
	);
}